import { useLocalStorage } from '@vueuse/core';
import { defineStore } from 'pinia';

import { PaymentType } from '@/types';

export const PAYMENT_TYPE_STORAGE_KEY = 'paymentTypeByInsertionId';
export const PAYMENT_TOTAL_STORAGE_KEY = 'paymentTotalByInsertionId';

export const usePaymentStore = defineStore('payment', () => {
  const paymentTypesByInsertionId = useLocalStorage<Record<string, PaymentType | undefined>>(
    PAYMENT_TYPE_STORAGE_KEY,
    {},
    { writeDefaults: false },
  );
  // Persisted because direct payment leaves the app for Datatrans and reloads it on return
  const paymentTotalsByInsertionId = useLocalStorage<Record<string, number | undefined>>(
    PAYMENT_TOTAL_STORAGE_KEY,
    {},
    { writeDefaults: false },
  );

  function getPaymentType(insertionId: string): PaymentType | undefined {
    if (!insertionId) return undefined;

    const paymentType = paymentTypesByInsertionId.value?.[insertionId];
    if (!paymentType || !Object.values(PaymentType).includes(paymentType)) {
      return undefined;
    }

    return paymentType;
  }

  function setPaymentType(insertionId: string, paymentType: PaymentType): void {
    if (!insertionId) return;

    paymentTypesByInsertionId.value = {
      ...paymentTypesByInsertionId.value,
      [insertionId]: paymentType,
    };
  }

  function clearPaymentType(insertionId: string): void {
    if (!insertionId || !paymentTypesByInsertionId.value[insertionId]) return;

    const { [insertionId]: _, ...nextPaymentTypes } = paymentTypesByInsertionId.value;
    paymentTypesByInsertionId.value = nextPaymentTypes;
  }

  /** Total the user saw on the billing summary when submitting, for the purchase event. */
  function getPaymentTotal(insertionId: string): number | undefined {
    if (!insertionId) return undefined;

    const total = paymentTotalsByInsertionId.value[insertionId];

    return typeof total === 'number' && Number.isFinite(total) ? total : undefined;
  }

  function setPaymentTotal(insertionId: string, total: number): void {
    if (!insertionId || !Number.isFinite(total)) return;

    paymentTotalsByInsertionId.value = {
      ...paymentTotalsByInsertionId.value,
      [insertionId]: total,
    };
  }

  function clearPaymentTotal(insertionId: string): void {
    if (!insertionId || paymentTotalsByInsertionId.value[insertionId] === undefined) return;

    const { [insertionId]: _, ...nextPaymentTotals } = paymentTotalsByInsertionId.value;
    paymentTotalsByInsertionId.value = nextPaymentTotals;
  }

  function $reset(): void {
    paymentTypesByInsertionId.value = {};
    paymentTotalsByInsertionId.value = {};
  }

  return {
    paymentTypesByInsertionId,
    paymentTotalsByInsertionId,
    getPaymentType,
    setPaymentType,
    clearPaymentType,
    getPaymentTotal,
    setPaymentTotal,
    clearPaymentTotal,
    $reset,
  };
});
